// The three sales PDFs as the screens link them (spec 09 §3.4, §3.6, §3.7;
// D-288). Pure, no React, unit-tested in __tests__/documents-kernel.test.ts.
//
// A PDF IS A PLAIN LINK, never a blob mutation. Each door answers a same-origin
// GET under the session cookie: the quote PDF and the delivery note 302 to a
// five-minute presigned URL, the proforma streams `application/pdf` inline. An
// `<a target="_blank">` lets the browser follow the redirect and carry the
// cookie on its own (the CoA preview link in quality/screens/coa-manager.tsx is
// the precedent). The paths are the API's own, same-origin, with no prefix: the
// generated client's `baseUrl` is "" for the same reason.
//
// A LINK EXISTS ONLY WHERE ITS DOOR ANSWERS 200 OR 302 (D-118). Each predicate
// below transcribes the door's refusal ladder from
// packages/contracts/src/sales/quote-register.ts, so a reader is never handed a
// tab full of problem JSON for a refusal the payload already predicted:
//
//   * quote PDF — 403 without `sales.read_value` (D-288 ruling 2: a priced
//     document cannot be redacted), 409 QUOTE_PDF_NOT_AVAILABLE for a quote
//     whose latest send stored no PDF: never sent, or sent before migration
//     0064. The detail says which through `pdf_available`, and that flag is
//     the ONE truth about whether the door serves (spec 09 §4.4 as ruled in
//     the PR-D4 fix round) — the status is not read. A quote revised after a
//     send reads `draft` and still has the PDF the customer holds (the door
//     serves the latest send of the sent revision), so a status gate would
//     hide a document the door answers for.
//   * proforma — 403 without `sales.read_value`, 409 ORDER_NOT_CONFIRMED while
//     `pending_approval`, 409 ORDER_TERMINAL once `cancelled` (ruling 19). A
//     CLOSED order renders: it is the record of what was billed.
//   * delivery note — `sales.read` alone (ruling 14: the storekeeper who
//     packed it prints it). A holder of `sales.read_value` is redirected to the
//     stored note, and any other reader is answered 200 with a copy drawn on
//     demand with no prices (D-337 ruling 4), so the link is offered to both
//     and only its label differs (`deliveryNoteUnpriced`). The door serves a
//     note WHENEVER one was drawn:
//     packed, shipped, delivered, and a shipment cancelled after its note was
//     drawn at pack (the note is frozen, and the cancel does not unmake it).
//     It answers 409 SHIPMENT_NOT_PACKED while `picking` and for a shipment
//     cancelled with no note, and 409 DELIVERY_NOTE_NOT_RENDERED for a packed
//     or later shipment whose file id is NULL.
//
// A NULL NOTE IS NOT A FAILED DRAW. Shipments packed before the renderer
// existed (migration 0064), and the seeded shipped and delivered ones, carry
// no note either, so the screens say "no delivery note on file" and never
// "it could not be drawn" (auditor S5). The re-render is the remedy for all of
// them alike.
//
// A GLYPH REFUSAL NAMES ITS CHARACTERS. 422 DOCUMENT_GLYPH_COVERAGE lists every
// character no bundled font can print in `code_points` (top-level on the
// problem body, as every RFC 9457 extension here is). The quote send and the
// delivery note's re-render (office and floor) both render it through
// `glyphCharactersSentence`, which prints each character AND its U+ code, so
// a manager can find it in the text that holds it. The proforma is a plain
// link that opens a new tab, so its 422 reaches the browser, not a screen.

import type { TFunction } from "i18next";
import { RequestError } from "../../../../lib/request";

/** The order statuses the proforma door refuses (D-288 ruling 19). */
const PROFORMA_REFUSED: ReadonlySet<string> = new Set(["pending_approval", "cancelled"]);

/** The shipment statuses a MISSING delivery note can be drawn on: it is
 * drawn at pack and frozen, so `packed` and everything a packed shipment moves
 * on to. A note that EXISTS is served on any status (a cancelled shipment's
 * included) — `deliveryNoteOffer` reads the file id first. */
export const NOTE_STATUSES: ReadonlySet<string> = new Set(["packed", "shipped", "delivered"]);

type Has = (code: string) => boolean;

export function quotePdfPath(quoteId: string): string {
  return `/v1/quotes/${encodeURIComponent(quoteId)}/pdf`;
}

export function proformaPath(orderId: string): string {
  return `/v1/orders/${encodeURIComponent(orderId)}/proforma.pdf`;
}

export function deliveryNotePath(shipmentId: string): string {
  return `/v1/shipments/${encodeURIComponent(shipmentId)}/delivery-note.pdf`;
}

/** Offer the quote's PDF link? Both arms of the door, and nothing else: a
 * send that stored its PDF (`pdf_available` — false on a never-sent quote and
 * on one sent before the renderer existed; true on a draft revised after a
 * send, whose customer still holds that PDF) and the `sales.read_value` a
 * priced document needs. The status is deliberately not read (§4.4). */
export function canOpenQuotePdf(quote: { readonly pdf_available: boolean }, has: Has): boolean {
  return quote.pdf_available && has("sales.read_value");
}

/** Offer the proforma link? Confirmed onward, never cancelled, and only to a
 * reader who may see the prices it prints. */
export function canOpenProforma(status: string, has: Has): boolean {
  return !PROFORMA_REFUSED.has(status) && has("sales.read_value");
}

/**
 * What one shipment row offers for its delivery note:
 *   * `link`    — a note is on file; open it. On ANY status: a shipment
 *                 cancelled after its note was drawn still serves it.
 *   * `render`  — packed or later with no note on file (a draw that failed
 *                 at pack — the pack stood, D-288 ruling 11 — or a shipment
 *                 packed before notes existed), and this reader may draw it:
 *                 `sales.shipment.post`, the code `POST /v1/shipments/{id}/
 *                 delivery-note` checks, AND a write scope that covers the
 *                 order's facility (`facilityWritable`, the order detail's
 *                 `facility_writable` — the door refuses 403 otherwise).
 *   * `missing` — the same state, for a reader who cannot draw it: a neutral
 *                 sentence, never a button whose press would 403.
 *   * `none`    — still picking, or cancelled with no note: there is no note
 *                 to speak of, and the re-render answers 409.
 */
export type DeliveryNoteOffer = "link" | "render" | "missing" | "none";

/** Does this reader's delivery-note link open the copy without prices?
 * (D-337 ruling 4.) The door decides on `sales.read_value` alone, whatever
 * `delivery_note_show_prices` was when the stored note was drawn, because
 * nothing records whether that note is priced. The label says which copy the
 * link opens. */
export function deliveryNoteUnpriced(has: Has): boolean {
  return !has("sales.read_value");
}

export function deliveryNoteOffer(
  shipment: { readonly status: string; readonly delivery_note_file_id: string | null },
  has: Has,
  facilityWritable: boolean,
): DeliveryNoteOffer {
  if (shipment.delivery_note_file_id !== null) return "link";
  if (!NOTE_STATUSES.has(shipment.status)) return "none";
  return has("sales.shipment.post") && facilityWritable ? "render" : "missing";
}

// ---------------------------------------------------------------------------
// 422 DOCUMENT_GLYPH_COVERAGE, as a sentence that names the characters.
// ---------------------------------------------------------------------------

/** How many characters the sentence names before it counts the rest: a
 * product description in a script the fonts lack can hold hundreds, and the
 * first few are enough to find the text that holds them. */
export const GLYPHS_NAMED = 10;

const CODE_POINT = /^U\+([0-9A-F]{4,6})$/u;

/**
 * The problem's `code_points`, read defensively: the member is untyped on the
 * wire (`Problem` declares no extensions), so anything but `"U+XXXX"` strings
 * is dropped. Takes the live array OR the floor outbox's persisted form, the
 * same codes joined by spaces (`OutboxFailure.extensions` holds strings only).
 */
export function codePointList(raw: unknown): string[] {
  const items: unknown[] = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? raw.split(/\s+/u)
      : [];
  return items.filter((item): item is string => typeof item === "string" && CODE_POINT.test(item));
}

/** The code points a live refusal named — empty for any other error. */
export function glyphCodePointsOf(error: unknown): string[] {
  if (!(error instanceof RequestError) || error.code !== "DOCUMENT_GLYPH_COVERAGE") return [];
  const problem = error.problem as Record<string, unknown> | undefined;
  return codePointList(problem?.["code_points"]);
}

/**
 * One character as the sentence prints it: the character itself, then its
 * code — `😀 (U+1F600)`. A combining mark is drawn on a dotted circle so it
 * has something to sit on (`◌́ (U+0301)`), and a character that draws nothing
 * (a control, a format character, a separator, a lone surrogate) is its code
 * alone: an empty pair of quotes would be a sentence naming nothing.
 */
export function glyphLabel(codePoint: string): string | undefined {
  const hex = CODE_POINT.exec(codePoint)?.[1];
  if (hex === undefined) return undefined;
  const value = Number.parseInt(hex, 16);
  if (value > 0x10ffff || (value >= 0xd800 && value <= 0xdfff)) return codePoint;
  const char = String.fromCodePoint(value);
  if (/^[\p{C}\p{Z}]$/u.test(char)) return codePoint;
  if (/^\p{M}$/u.test(char)) return `\u25CC${char} (${codePoint})`;
  return `${char} (${codePoint})`;
}

/**
 * The sentence naming what the fonts cannot print, for a refusal's code
 * points; the unnamed sentence when the refusal carried none it could read.
 * Callers append their own sentence saying where the characters come from —
 * the quote, or the order — each as a literal key the i18n gate can see.
 */
export function glyphCharactersSentence(t: TFunction, codePoints: readonly string[]): string {
  const labels = codePoints.map(glyphLabel).filter((label): label is string => label !== undefined);
  if (labels.length === 0) return t("sales.documents.glyphsUnnamed");
  const named = labels.slice(0, GLYPHS_NAMED).join(", ");
  const characters =
    labels.length > GLYPHS_NAMED
      ? t("sales.documents.glyphsMore", { list: named, rest: labels.length - GLYPHS_NAMED })
      : named;
  return t("sales.documents.glyphsNamed", { characters });
}
